import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight, ShieldCheck, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Reveal } from "@/components/effects/Reveal";
import { LightStreaks } from "@/components/effects/LightStreaks";
import { BokehParticles } from "@/components/effects/BokehParticles";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Even if the server somehow errors, treat as generic confirmation to
      // avoid leaking whether the account exists. Surface only rate-limit messages.
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
      } else {
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-forgot-password" className="relative min-h-[100svh] overflow-hidden flex items-center justify-center py-16">
      <SEO title="Forgot Password · WelkomBiz Photography" description="Reset your WelkomBiz Photography account password securely." canonicalPath="/forgot-password" noindex />
      <div className="absolute inset-0 -z-10" style={{
        background:
          "radial-gradient(70% 60% at 50% 0%, rgba(255,46,147,0.12) 0%, transparent 60%)," +
          "radial-gradient(70% 60% at 50% 100%, rgba(24,211,255,0.10) 0%, transparent 60%)," +
          "linear-gradient(180deg, #000, #050507 50%, #000)",
      }} />
      <LightStreaks count={4} />
      <BokehParticles count={10} />

      <div className="relative w-full max-w-[460px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-7">
            <Logo size={56} linkTo="/" />
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass chrome-border">
              <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
              <span className="text-[10px] tracking-luxe uppercase text-white/70">Account Recovery</span>
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl">
              <span className="text-chrome">Forgot your </span>
              <span className="italic text-magcyan">password</span>
              <span className="text-chrome">?</span>
            </h1>
            <p className="mt-4 text-sm text-white/55 font-body max-w-sm">
              Enter the email tied to your WelkomBiz Photography account and we'll send you a secure reset link.
            </p>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <form onSubmit={submit} data-testid="forgot-password-form" className="glass-strong chrome-border rounded-md p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />

            {sent ? (
              <div data-testid="forgot-password-success" className="relative text-center space-y-3 py-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff2e93] to-[#18d3ff] mx-auto flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <h2 className="font-display text-xl text-chrome">Check your inbox</h2>
                <p className="text-sm text-white/65 font-body">
                  If an account exists for this email address, password reset instructions will be sent.
                </p>
                <p className="text-[11px] text-white/45">
                  The reset link expires in 30 minutes for your security.
                </p>
                <Link to="/auth" data-testid="forgot-password-back-login" className="btn-ghost !py-2.5 !px-5 !text-[11px] mt-3 inline-flex">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to login
                </Link>
              </div>
            ) : (
              <div className="relative space-y-4">
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
                    <Mail className="w-3 h-3" /> Email Address
                  </span>
                  <input
                    data-testid="forgot-password-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
                  />
                </label>

                {error && <p data-testid="forgot-password-error" className="text-xs text-[#ff9fcf]">{error}</p>}

                <button type="submit" data-testid="forgot-password-submit" disabled={busy || !email} className="btn-primary w-full mt-2 disabled:opacity-60">
                  {busy ? "Sending…" : "Send Reset Link"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <div className="text-center pt-1">
                  <Link to="/auth" data-testid="forgot-password-back" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to login
                  </Link>
                </div>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </div>
  );
}
